import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import createTrack from 'services/actions/tracker_actions';
import { createUserQuestion, updateUserQuestion } from 'store/modules/bidding_chat/actions';
import { BiddingChatUserSavedQuestionContentTypes } from 'store/modules/bidding_chat/types';

interface SavedQuestionInputProps {
  question: BiddingChatUserSavedQuestionContentTypes | null;
  setEditingId: React.Dispatch<React.SetStateAction<number | null>>;
  setIsLoadingSaveQuestion: React.Dispatch<React.SetStateAction<boolean>>;
}

const SavedQuestionInput = ({
  question,
  setEditingId,
  setIsLoadingSaveQuestion,
}: SavedQuestionInputProps) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const [messageText, setMessageText] = useState<string>('');

  const dispatch = useDispatch();

  useEffect(() => {
    const initialText = question?.content ?? '';
    setMessageText(initialText);

    if (componentRef.current) {
      componentRef.current.innerText = initialText;
    }
  }, [question?.id]);

  const handleClick = () => {
    if (!messageText.trim()) return;

    if (question === null) {
      createTrack('Pergunte ao Edital - criou pergunta salva');
      dispatch(createUserQuestion(messageText));
      setIsLoadingSaveQuestion(true);

      setTimeout(() => {
        setMessageText('');
        if (componentRef.current) componentRef.current.innerText = '';
      }, 0);
      return;
    }

    createTrack('Pergunte ao Edital - editou pergunta salva');
    dispatch(updateUserQuestion(messageText, question.id));
    setEditingId(null);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    document.execCommand('inserttext', false, event.clipboardData.getData('text/plain'));
  };

  const saveButton = (
    <button
      type="button"
      className={`databalloon btn btn-link pb-0 flex-shrink-0 ${
        question === null ? 'px-2' : 'bidding-chat-saved-question-action-btn'
      }`}
      aria-label="Clique para salvar a pergunta"
      data-balloon-pos="left"
      disabled={!messageText.trim()}
      onClick={() => handleClick()}
    >
      {question === null ? (
        <span className="material-icons">done</span>
      ) : (
        <i className="material-icons-outlined" aria-hidden>
          done
        </i>
      )}
    </button>
  );

  const textInput = (
    <div
      ref={componentRef}
      aria-label={question === null ? 'Escreva sua pergunta' : 'Editar pergunta salva'}
      tabIndex={0}
      role="textbox"
      contentEditable
      className={`btn btn-link text-left bidding-chat-saved-question-inline-input flex-grow-1 min-w-0 ${
        messageText ? 'hide-content' : ''
      }`}
      onPaste={handlePaste}
      onInput={(e) => setMessageText((e.target as HTMLDivElement).innerText)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleClick();
        }
      }}
    />
  );

  if (question === null) {
    return (
      <div className="bidding-chat-saved-new-question-wrap w-100 bidding-chat-saved-new-question-wrap--create">
        <div className="bidding-chat-saved-new-question-inner bidding-chat-saved-new-question-inner-row d-flex align-items-center">
          {textInput}
          {saveButton}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bidding-chat-saved-question-list-edit-field flex-grow-1 min-w-0">
        <div className="bidding-chat-saved-new-question-inner bidding-chat-saved-new-question-inner-row bidding-chat-saved-new-question-inner--list-edit d-flex align-items-center w-100">
          {textInput}
        </div>
      </div>
      <div className="d-flex flex-shrink-0 align-items-center bidding-chat-saved-question-line-actions">
        {saveButton}
        <button
          type="button"
          tabIndex={-1}
          className="databalloon btn btn-link pb-0 flex-shrink-0 invisible bidding-chat-saved-question-action-btn-spacer"
          aria-hidden="true"
          disabled
        >
          <i className="material-icons-outlined" aria-hidden>
            delete_outline
          </i>
        </button>
      </div>
    </>
  );
};

export default SavedQuestionInput;
